import { Link, useParams } from "react-router-dom";
import { usePublicShareReport } from "@/hooks/useShareReport";
import { ApiError } from "@/lib/api";
import ReportView from "@/components/ReportView";
import { currentBrand } from "@/lib/brand";

/**
 * Public, token-gated dashboard. No auth, no app chrome.
 * Brand info on the snapshot comes from the source site (gocfo, etc.) so a
 * link generated on gocfo.co.uk still reads "GoCFO" even if the recipient
 * opens it on golandlord.co.uk.
 */
export default function SharedReport() {
  const { token } = useParams<{ token: string }>();
  const query = usePublicShareReport(token);
  const fallbackBrand = currentBrand();

  const status = query.isLoading
    ? "loading"
    : query.isError
      ? (query.error as ApiError | undefined)?.status === 404 ||
        (query.error as ApiError | undefined)?.status === 403
        ? "expired"
        : "error"
      : "ok";

  const brandName =
    query.data?.site?.name ?? fallbackBrand.name;
  const brandUrl = query.data?.site?.domain
    ? `https://${query.data.site.domain}`
    : fallbackBrand.marketingUrl;

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="border-b">
        <div className="container flex h-14 items-center justify-between">
          <a href={brandUrl} className="font-semibold">
            {brandName}
          </a>
          <Link
            to="/dashboard/signup"
            className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:opacity-90"
          >
            Build your own
          </Link>
        </div>
      </header>
      <main className="container py-10">
        {status === "loading" ? (
          <p className="text-muted-foreground">Loading shared report…</p>
        ) : status === "expired" ? (
          <ExpiredOrMissing />
        ) : status === "error" ? (
          <p className="text-destructive">
            Could not load this report. Please try again later.
          </p>
        ) : (
          <ReportView
            company={query.data!.snapshot.company}
            financials={query.data!.snapshot.financials}
            isPublic
            generatedAt={query.data!.snapshot.generated_at}
            brandName={brandName}
            brandUrl={brandUrl}
          />
        )}
      </main>
    </div>
  );
}

function ExpiredOrMissing() {
  return (
    <div className="mx-auto max-w-md rounded-lg border p-8 text-center">
      <h1 className="text-lg font-semibold">Report not available</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        This shared link has expired or never existed. Ask whoever sent you here to
        generate a fresh one.
      </p>
      <Link
        to="/dashboard/signup"
        className="mt-6 inline-block rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90"
      >
        Build your own dashboard
      </Link>
    </div>
  );
}
